/**
 * Browser side of the WebAuthn ceremonies for Laravel Fortify's passkeys.
 *
 * The endpoints are the ones Fortify registers, see
 * https://laravel.com/docs/fortify#passkeys
 */

export const passkeysSupported = () =>
    typeof window.PublicKeyCredential !== 'undefined' &&
    typeof window.PublicKeyCredential.parseRequestOptionsFromJSON === 'function';

const messageFrom = (error) =>
    error?.response?.data?.errors?.credential?.[0] ||
    error?.response?.data?.message ||
    error?.message ||
    'Unknown error';

/**
 * Sign in with a discoverable passkey; no username needed.
 */
export async function loginWithPasskey(remember = false) {
    const { data } = await window.axios.get('/passkeys/login/options');

    const credential = await navigator.credentials.get({
        publicKey: PublicKeyCredential.parseRequestOptionsFromJSON(data.options),
    });

    const response = await window.axios.post('/passkeys/login', {
        credential: credential.toJSON(),
        remember,
    });

    window.location.href = response.data?.redirect || '/home';
}

/**
 * Register a new passkey for the signed in user.
 */
export async function registerPasskey(name) {
    const { data } = await window.axios.get('/user/passkeys/options');

    const credential = await navigator.credentials.create({
        publicKey: PublicKeyCredential.parseCreationOptionsFromJSON(data.options),
    });

    await window.axios.post('/user/passkeys', {
        name,
        credential: credential.toJSON(),
    });

    window.location.reload();
}

/**
 * Turn an error of one of the calls above into a message for the user.
 *
 * Aborting the browser dialog is not worth an error message, and an expired
 * password confirmation has to send the user to the confirmation screen.
 */
export function handlePasskeyError(error, fallback) {
    if (error?.name === 'NotAllowedError' || error?.name === 'AbortError') {
        return null;
    }

    if (error?.response?.status === 423) {
        window.location.href = '/user/confirm-password';

        return null;
    }

    return fallback ? `${fallback}: ${messageFrom(error)}` : messageFrom(error);
}
